import {
  Accordion,
  CardContent,
  CardDescription,
  CardFooter,
  InfiniteScroll,
  Input,
  LoadingSpinner,
  ScrollArea,
} from '@ais-ui/ui-core';
import React, { useState } from 'react';
import { AtonActionsMenu } from './AtonActionsMenu';
import { useLocalization } from '@ais-ui/locale-management';
import { NoItemsEmptyStateList } from '../../components/EmptyList';
import { useUnifiedAtons } from './useUnifiedAtons';
import { useAtonFilters } from './useAtonFilters';
import { AtonSort } from './AtonSort';
import { AtonFilter } from './AtonFilter';
import { AtonItem } from './items/AtonItem';

interface AtonListProps {
  requiresMap: boolean;
}

export const AtonList = React.memo(({ requiresMap }: AtonListProps) => {
  const { messages } = useLocalization();

  const [search, setSearch] = useState<string>('');
  const { unifiedAtons, isLoading } = useUnifiedAtons(''); // Pass empty search to get all data

  const {
    filteredAndSortedAtons,
    atonFilters,
    setAtonFilters,
    sortConfig,
    setSortConfig,
    availableOwners,
  } = useAtonFilters(unifiedAtons, search);

  return (
    <>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            id="search"
            placeholder={messages.atonListFindAton}
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <AtonSort sortConfig={sortConfig} setSortConfig={setSortConfig} />
          <AtonFilter
            atonFilters={atonFilters}
            setAtonFilters={setAtonFilters}
            availableOwners={availableOwners}
          />
        </div>
      </CardContent>
      <CardContent className="flex-1 flex-grow-1 flex flex-col overflow-hidden pt-0">
        {requiresMap && !isLoading && unifiedAtons?.length === 0 && (
          <NoItemsEmptyStateList entity="aton" srcImage="empty_list_bro.png">
            <AtonActionsMenu
              buttonText="Create AtoN"
              className="w-full h-10"
            ></AtonActionsMenu>
          </NoItemsEmptyStateList>
        )}
        {isLoading && <LoadingSpinner></LoadingSpinner>}
        {!isLoading && unifiedAtons?.length !== 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <InfiniteScroll
              items={filteredAndSortedAtons}
              limit={30}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              renderItem={(aton) => {
                return (
                  <AtonItem
                    key={aton.id}
                    aton={aton}
                    requiresMap={requiresMap}
                  ></AtonItem>
                )
              }}
              >
            </InfiniteScroll>
          </Accordion>
        )}
      </CardContent>
      <CardFooter className="flex flex-grow-0 flex-col items-end min-h-52">
        {requiresMap && unifiedAtons.length !== 0 && (
          <AtonActionsMenu
            buttonText={messages.atonActionsMenuButtonLabel}
          ></AtonActionsMenu>
        )}
      </CardFooter>
    </>
  );
});

AtonList.displayName = 'AtonList';
